import { CardBrand, PaymentMethod } from "@next-platform/client-common-core";
import type { TFunction } from "i18next";

import { BASE_PAYMENT_METHOD_CLASS } from "@/constants/subscriptions";
import { PaymentMethodIconConfig } from "@/types/subscription";

export type ResolvedPaymentConfig = {
  label: string;
  description: string;
  icon: PaymentMethodIconConfig;
  className?: string;
  isMostPopular?: boolean;
};

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const getCorePaymentMethodConfig = (t: TFunction): Partial<Record<PaymentMethod, ResolvedPaymentConfig>> => ({
  [PaymentMethod.CARD]: {
    label: t("subscription:checkout.paymentMethod.creditCard"),
    description: "",
    icon: { type: "iconify", name: "icon-park-outline:bank-card", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.VOLT]: {
    label: t("subscription:checkout.paymentMethod.payByBank"),
    description: t("subscription:checkout.paymentMethod.oneTimePayment"),
    icon: { type: "iconify", name: "mdi:bank", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.DIRECTA24]: {
    label: t("subscription:checkout.paymentMethod.payByBank"),
    description: t("subscription:checkout.paymentMethod.oneTimePayment"),
    icon: { type: "iconify", name: "mdi:bank", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.INWIZO_ECOM]: {
    label: t("subscription:checkout.paymentMethod.creditCard"),
    description: t("subscription:checkout.paymentMethod.oneTimePayment"),
    icon: { type: "iconify", name: "material-symbols:payments-outline", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.INWIZO_TPAY]: {
    label: t("subscription:checkout.paymentMethod.payment", { method: "Telco" }),
    description: t("subscription:checkout.paymentMethod.oneTimePayment"),
    icon: { type: "iconify", name: "material-symbols:payments-outline", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.OVERPAY]: {
    label: "СБП",
    description: t("subscription:checkout.paymentMethod.cardsAndFastTransfers"),
    icon: { type: "card", name: "sbp", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.INWIZO_SBP]: {
    label: "Перевод через Сбербанк",
    description: t("subscription:checkout.paymentMethod.p2pTransfer"),
    icon: { type: "iconify", name: "mdi:bank", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.LUXFIN]: {
    label: "VENMO (pyusd)",
    description: t("subscription:checkout.paymentMethod.oneTimePayment"),
    icon: { type: "iconify", name: "cib:venmo", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
});

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const getWalletPaymentMethodConfig = (t: TFunction): Partial<Record<PaymentMethod, ResolvedPaymentConfig>> => ({
  [PaymentMethod.PAYOK]: {
    label: "UPI (Backup Provider)",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₹" }),
    icon: { type: "iconify", name: "material-symbols:upi-pay-outline-sharp", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.UPGATE_ALIPAY]: {
    label: "Alipay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "¥" }),
    icon: { type: "card", name: "alipay", height: 30, width: 30, color: "white" },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.UPGATE_KAKAO]: {
    label: "Kakao Pay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₩" }),
    icon: { type: "card", name: "kakaopay", height: 30, width: 30, color: "white" },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.UPGATE_UPI]: {
    label: "UPI (PhonePe, Paytm, GPay)",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₹" }),
    icon: { type: "iconify", name: "material-symbols:upi-pay-outline-sharp", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.FPAY_WECHAT]: {
    label: "WeChat Pay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "¥" }),
    icon: { type: "card", name: "wechatpay", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.FPAY_ALIPAY]: {
    label: "Alipay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "¥" }),
    icon: { type: "card", name: "alipay", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.COINPAYMENTS]: {
    label: "Cryptocurrencies",
    description: t("subscription:checkout.paymentMethod.oneTimePayment"),
    icon: { type: "iconify", name: "bitcoin-icons:bitcoin-circle-outline", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
});

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const getPayssionRegionalPaymentMethodConfig = (t: TFunction): Partial<Record<PaymentMethod, ResolvedPaymentConfig>> => ({
  [PaymentMethod.PAYSSION_GCASH_PH]: {
    label: "GCash",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₱" }),
    icon: { type: "card", name: "payssion-gcash", height: 22, width: 26 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.PAYSSION_PAYMAYA_PH]: {
    label: "Maya",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₱" }),
    icon: { type: "card", name: "payssion-paymaya", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.PAYSSION_SBP_RU]: {
    label: "СБП",
    description: t("subscription:checkout.paymentMethod.cardsAndFastTransfers"),
    icon: { type: "card", name: "sbp", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.PAYSSION_SBERPAY_RU]: {
    label: "SberPay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₽" }),
    icon: { type: "card", name: "payssion-sberpay", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.PAYSSION_TBANK_RU]: {
    label: "T-Pay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₽" }),
    icon: { type: "card", name: "payssion-tbank", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.PAYSSION_YOOMONEY_RU]: {
    label: "YooMoney",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₽" }),
    icon: { type: "card", name: "payssion-yoomoney", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.PAYSSION_CARD_RU]: {
    label: t("subscription:checkout.paymentMethod.russianCards"),
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₽" }),
    icon: { type: "card", name: "mir", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.DUKPAY_SBP_RU]: {
    label: "СБП",
    description: t("subscription:checkout.paymentMethod.cardsAndFastTransfers"),
    icon: { type: "card", name: "sbp", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.DUKPAY_SBERPAY_RU]: {
    label: "SberPay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₽" }),
    icon: { type: "card", name: "sberpay", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.DUKPAY_TBANK_RU]: {
    label: "T-Pay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₽" }),
    icon: { type: "card", name: "tbank", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.DUKPAY_YOOMONEY_RU]: {
    label: "YooMoney",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₽" }),
    icon: { type: "card", name: "yoomoney", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.DUKPAY_CARD_RU]: {
    label: t("subscription:checkout.paymentMethod.russianCards"),
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₽" }),
    icon: { type: "card", name: "mir", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
});

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const getPayssionInternationalPaymentMethodConfig = (t: TFunction): Partial<Record<PaymentMethod, ResolvedPaymentConfig>> => ({
  [PaymentMethod.PAYSSION_KAKAOPAY_KR]: {
    label: "KakaoPay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₩" }),
    icon: { type: "card", name: "kakaopay", height: 30, width: 30, color: "white" },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.PAYSSION_SAMSUNGPAY_KR]: {
    label: "SamsungPay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "₩" }),
    icon: { type: "card", name: "payssion-samsungpay", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.PAYSSION_VIETQR_VN]: {
    label: "VietQR",
    description: "Thanh toán một lần (₫)",
    icon: { type: "card", name: "payssion-vietqr", height: 10, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.PAYSSION_ENETS_SG]: {
    label: "Enets",
    description: "一次性付款 (SGD)",
    icon: { type: "card", name: "payssion-enets", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.PAYSSION_PAYNOW_SG]: {
    label: "Paynow",
    description: "一次性付款 (SGD)",
    icon: { type: "card", name: "payssion-paynow", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.PAYSSION_STCPAY_SA]: {
    label: "STCPay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "SAR" }),
    icon: { type: "card", name: "payssion-stcpay", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
  [PaymentMethod.PAYSSION_APPLEPAY_SA]: {
    label: "ApplePay",
    description: t("subscription:checkout.paymentMethod.oneTimePaymentWithCurrency", { currency: "SAR" }),
    icon: { type: "card", name: "payssion-applepay", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
    isMostPopular: true,
  },
});

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const getStixrampPaymentMethodConfig = (t: TFunction): Partial<Record<PaymentMethod, ResolvedPaymentConfig>> => ({
  [PaymentMethod.STIXRAMP_CASH_APP_US]: {
    label: "Cash App",
    description: t("subscription:checkout.paymentMethod.oneTimePayment"),
    icon: { type: "card", name: "cashapp", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
  [PaymentMethod.STIXRAMP_PAYPAL_US]: {
    label: "PayPal",
    description: t("subscription:checkout.paymentMethod.oneTimePayment"),
    icon: { type: "card", name: "paypal", height: 30, width: 30 },
    className: BASE_PAYMENT_METHOD_CLASS,
  },
});

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
export const getPaymentMethodConfig = (t: TFunction): Partial<Record<PaymentMethod, ResolvedPaymentConfig>> => ({
  ...getCorePaymentMethodConfig(t),
  ...getWalletPaymentMethodConfig(t),
  ...getPayssionRegionalPaymentMethodConfig(t),
  ...getPayssionInternationalPaymentMethodConfig(t),
  ...getStixrampPaymentMethodConfig(t),
});

export const CARD_BRAND_CONFIG: Record<CardBrand, ResolvedPaymentConfig> = {
  [CardBrand.VISA]: {
    label: "Visa",
    description: "",
    icon: { type: "card", name: "visa", height: 24, width: 40 },
    isMostPopular: true,
  },
  [CardBrand.MASTER]: {
    label: "MasterCard",
    description: "",
    icon: { type: "card", name: "master", height: 24, width: 40 },
    isMostPopular: true,
  },
  [CardBrand.DISCOVER]: {
    label: "Discover",
    description: "",
    icon: { type: "card", name: "discover", height: 24, width: 40 },
  },
};
